import { configureStore } from '@reduxjs/toolkit'
import createSagaMiddleware from 'redux-saga'
import { IApi } from '../api'
import apiMiddleware from './apiMiddleware'
import channelMembershipMiddleware from './channelMembershipMiddleware'
import crossTabSessionMiddleware from './crossTabSessionMiddleware'
import messageAlertsMiddleware from './messageAlertsMiddleware'
import muteExpiryMiddleware from './muteExpiryMiddleware'
import offlineSnapshotMiddleware from './offlineSnapshotMiddleware'
import outboxMiddleware from './outboxMiddleware'
import reducer from './reducer'
import rootSaga from './sagas'
import threadsWindowMiddleware from './threadsWindowMiddleware'
import webSocketMiddleware from './webSocketMiddleware'

export default function createAppStore(api: IApi) {
  const sagaMiddleware = createSagaMiddleware()

  const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: false,
        serializableCheck: false,
      }).concat(
        // Ahead of apiMiddleware, which runs the request this fills in.
        threadsWindowMiddleware(),
        apiMiddleware(api),
        // Attaches the `storage` listener that keeps this tab in step with the session the
        // rest of the browser is in. Ordered next to apiMiddleware because it is the other
        // half of the same concern: apiMiddleware reacts to a token being refused, this
        // reacts to one being replaced, and reacting to the replacement first is what stops
        // the refusal happening at all.
        crossTabSessionMiddleware(api),
        webSocketMiddleware(),
        // After webSocketMiddleware (which dispatches the socket events) and before
        // the reducer sees them, so it can read "which conversation was open" as it
        // was when the message landed.
        messageAlertsMiddleware(),
        // Same reasoning as messageAlertsMiddleware: needs to see a membership
        // event before the (narrower) reducer case does, so it can decide whether
        // the sidebar already knows about that channel.
        channelMembershipMiddleware(),
        // Re-derives badges and totals when a timed mute runs out.
        muteExpiryMiddleware(),
        // Persists unsent AIM messages and resends them when the connection returns.
        outboxMiddleware(),
        // Keeps the on-device AIM snapshot current, and wipes it on sign-out.
        offlineSnapshotMiddleware(api),
        sagaMiddleware,
      ),
    devTools: process.env.NODE_ENV !== 'production',
  })

  sagaMiddleware.run(() => rootSaga(api))

  return store
}
